import { CalendarFilled } from '@ant-design/icons'
import { ConfigProvider, DatePicker, Flex, Input, message, Spin } from 'antd'
import locale from 'antd/es/date-picker/locale/ko_KR'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import Swal from 'sweetalert2'
import {
  completeStandaloneScheduledTask,
  createStandaloneScheduledTask,
  deleteStandaloneScheduledTask,
  getStandaloneScheduledTask,
  incompleteStandaloneScheduledTask,
  toggleScheduledTaskTodoItem,
  updateStandaloneScheduledTask,
} from '../../../api/standalone.api'
import ColorDropDown from '../../../components/apos/ColorDropDown'
import AopsCommonModalLayout from '../../../components/apos/modal/AopsCommonModalLayout'
import ModalStatusButtonGroup from '../../../components/apos/modal/ModalStatusButtonGroup'
import { userState } from '../../../store/user'
import FormSection from '../ui/FormSection'
import SubTaskManager from '../ui/SubTaskManager'

const StandaloneScheduledTaskPopup = ({ id, open, onClose, onSuccess }) => {
  const [user] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    scheduledDate: dayjs(),
    completed: false,
    createdByName: '',
    subTasks: [],
    colorCode: '#FFC700',
  })

  const isEditMode = !!id

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // 상세 정보 조회
  const fetchScheduledTaskDetail = async () => {
    if (!id) return

    setIsLoading(true)
    try {
      const res = await getStandaloneScheduledTask(id)
      console.log('getStandaloneScheduledTask res::', res)
      if (res.status === 200) {
        const data = res.data
        const scheduledTask = data.scheduledTask || {}

        // todoItems를 subTasks 형태로 변환 (checked -> isDone)
        const subTasks =
          data.todoItems?.map((item) => ({
            id: item.id || `existing-${Date.now()}-${Math.random()}`,
            title: item.title || '',
            isDone: item.checked || item.isChecked || false,
          })) || []

        setFormData({
          name: scheduledTask.title || '',
          scheduledDate: scheduledTask.scheduledDate
            ? dayjs(scheduledTask.scheduledDate)
            : dayjs(),
          completed: scheduledTask.completed || false,
          createdByName: scheduledTask.createdByName || '',
          subTasks: subTasks,
          colorCode: scheduledTask.colorCode || '#FFC700',
        })
      } else {
        message.error('업무 상세 조회에 실패했습니다.')
      }
    } catch (error) {
      console.error('fetchScheduledTaskDetail error::', error)
      message.error('업무 상세 조회에 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      if (id) {
        fetchScheduledTaskDetail()
      } else {
        setFormData({
          name: '',
          scheduledDate: dayjs(),
          completed: false,
          createdByName: '',
          subTasks: [],
          colorCode: '#FFC700',
        })
      }
    }
  }, [open, id])

  const isFormValid = () => {
    // 필수 항목 검증
    const isNameValid = formData.name.trim() !== ''
    const isDateValid =
      formData.scheduledDate !== null && formData.scheduledDate !== undefined

    return isNameValid && isDateValid
  }

  const handleSubmit = async () => {
    if (!isFormValid()) {
      message.error('필수 항목을 모두 입력해주세요.')
      return
    }

    try {
      const param = {
        salusUserId: user.id,
        title: formData.name,
        scheduledDate: dayjs(formData.scheduledDate).format(
          'YYYY-MM-DD 23:59:59',
        ),
        colorCode: formData.colorCode,
      }

      if (isEditMode) {
        // 수정 모드: todoItems를 { title, isChecked } 형태로 변환
        param.todoItems = formData.subTasks.map((subTask) => ({
          title: subTask.title,
          isChecked: subTask.isDone ?? false,
        }))
      } else {
        // 생성 모드: 제목만 리스트로 전송
        param.todoItems = formData.subTasks.map((subTask) => subTask.title)
      }

      if (isEditMode) {
        console.log('updateStandaloneScheduledTask param::', param)
        // 수정 모드
        const res = await updateStandaloneScheduledTask(id, param)
        console.log('updateStandaloneScheduledTask res::', res)
        if (res.status === 200) {
          message.success('Scheduled 업무가 수정되었습니다.')
          // 수정 성공 시 초기 체크리스트 ID 업데이트
          // setInitialChecklistId(formData.checklistId)
          onSuccess?.()
          onClose()
        } else {
          message.error('Scheduled 업무 수정에 실패했습니다.')
        }
      } else {
        console.log('createStandaloneScheduledTask param::', param)
        // 생성 모드
        const res = await createStandaloneScheduledTask(param)
        console.log('createStandaloneScheduledTask res::', res)
        if (res.status === 200) {
          message.success('Scheduled 업무가 생성되었습니다.')
          onSuccess?.()
          onClose()
        } else {
          message.error('Scheduled 업무 생성에 실패했습니다.')
        }
      }
    } catch (error) {
      console.error('error::', error)
      message.error(
        `Scheduled 업무 ${isEditMode ? '수정' : '생성'}에 실패했습니다.`,
      )
    }
  }

  const handleDelete = async () => {
    if (!id) return

    const result = await Swal.fire({
      title: 'Scheduled 업무 삭제',
      text: '정말 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    })

    if (result.isConfirmed) {
      try {
        const res = await deleteStandaloneScheduledTask(id)
        if (res.status === 200) {
          message.success('Scheduled 업무가 삭제되었습니다.')
          onSuccess?.()
          onClose()
        } else {
          message.error('Scheduled 업무 삭제에 실패했습니다.')
        }
      } catch (error) {
        console.error('handleDelete error::', error)
        message.error('Scheduled 업무 삭제에 실패했습니다.')
      }
    }
  }

  // 하위업무 체크 토글
  const handleToggleSubTask = async (todoItemId) => {
    if (!user?.id || !isEditMode) return
    if (formData.completed) {
      message.warning('완료된 업무의 하위업무 상태는 변경할 수 없습니다.')
      return
    }

    try {
      const res = await toggleScheduledTaskTodoItem(todoItemId, user.id)
      if (res.status === 200) {
        // 로컬 상태 업데이트
        setFormData((prev) => ({
          ...prev,
          subTasks: prev.subTasks.map((item) =>
            item.id === todoItemId
              ? { ...item, isDone: !(item.isDone ?? false) }
              : item,
          ),
        }))
        onSuccess?.() // refetch tracker board
      }
    } catch (error) {
      console.error('handleToggleSubTask error::', error)
      message.error('하위업무 상태 변경에 실패했습니다.')
    }
  }

  // 하위업무가 모두 완료되었는지 확인
  const isAllSubTasksCompleted = () => {
    if (!formData.subTasks || formData.subTasks.length === 0) {
      return true // 하위업무가 없으면 완료로 간주
    }
    return formData.subTasks.every((item) => item.isDone ?? false)
  }

  const handleComplete = async () => {
    if (!id || !user?.id) return

    // 하위업무가 모두 완료되지 않았으면 완료 불가
    if (!formData.completed && !isAllSubTasksCompleted()) {
      message.warning('하위업무를 모두 완료한 후에 완료 눌러주세요.')
      return
    }

    try {
      if (formData.completed) {
        // 미완료로 변경
        const res = await incompleteStandaloneScheduledTask(id, user.id)
        if (res.status === 200) {
          message.success('Scheduled 업무가 미완료로 변경되었습니다.')
          setFormData((prev) => ({ ...prev, completed: false }))
          onSuccess?.()
          onClose()
        }
      } else {
        // 완료로 변경
        const res = await completeStandaloneScheduledTask(id, user.id)
        if (res.status === 200) {
          message.success('Scheduled 업무가 완료되었습니다.')
          setFormData((prev) => ({ ...prev, completed: true }))
          onSuccess?.()
          onClose()
        }
      }
    } catch (error) {
      console.error('handleComplete error::', error)
      message.error('업무 상태 변경에 실패했습니다.')
    }
  }

  return (
    <ConfigProvider
      locale={{
        ...locale,
        DatePicker: locale,
      }}
    >
      <AopsCommonModalLayout
        title={isEditMode ? 'Scheduled 업무 상세' : 'Scheduled 업무 생성하기'}
        onClose={onClose}
        open={open}
      >
        <Spin spinning={isLoading}>
          <div className="form-container standalone-task-popup">
            <div className="form-wrapper">
              {/* 명칭 입력 */}
              <FormSection
                label="업무 명"
                required
                helperTitle="Scheduled 업무란?"
                helperDesc={
                  '반복되지는 않지만, 정해진 시점에 반드시 처리해야 하는 단일 일정 업무'
                }
              >
                <Flex gap="8px">
                  <ColorDropDown
                    value={formData.colorCode}
                    defaultValue={formData.colorCode}
                    onChange={(value) => updateField('colorCode', value)}
                    disabled={isEditMode && formData.completed}
                  />
                  <Input
                    placeholder="업무명을 입력해주세요"
                    className={`form-input ${isEditMode && formData.completed ? 'is-disabled' : ''}`}
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    maxLength={99}
                    disabled={isEditMode && formData.completed}
                    readOnly={isEditMode && formData.completed}
                  />
                </Flex>
              </FormSection>

              {/* 일정 */}
              <FormSection label="일정" required>
                <div style={{ display: 'flex' }}>
                  <DatePicker
                    disabledDate={(current) =>
                      current && current < dayjs().startOf('day')
                    }
                    style={{ flex: 0.4, height: '48px' }}
                    placeholder="날짜를 선택해주세요"
                    suffixIcon={<CalendarFilled />}
                    value={formData.scheduledDate}
                    onChange={(date) => updateField('scheduledDate', date)}
                    readOnly={true}
                    format="YYYY-MM-DD"
                    disabled={isEditMode && formData.completed}
                  />
                </div>
              </FormSection>

              {/* 담당자 (수정 모드일 때만) */}
              {isEditMode && formData.createdByName && (
                <FormSection
                  label="담당자"
                  required
                  className="assignee-section"
                >
                  <ModalStatusButtonGroup
                    options={[formData.createdByName]}
                    value={formData.createdByName}
                    onChange={() => {}}
                    isRound={true}
                    disabled={true}
                  />
                </FormSection>
              )}

              {/* 체크리스트 */}
              {/* {isEditMode
                ? renderEditModeChecklist()
                : renderCreateModeChecklist()} */}

              {/* 하위업무 */}
              <FormSection label="하위업무">
                <SubTaskManager
                  subTasks={formData.subTasks}
                  onSubTasksChange={(newSubTasks) =>
                    setFormData((prev) => ({ ...prev, subTasks: newSubTasks }))
                  }
                  onToggleCheck={isEditMode ? handleToggleSubTask : undefined}
                  disabled={isEditMode && formData.completed}
                  subTitle="이 테스크에서 체크해야 할 업무를 적어보세요"
                />
              </FormSection>
            </div>
          </div>

          {/* 수정 모드: 완료 버튼 + 삭제/수정 버튼 */}
          {isEditMode && (
            <div className="popup-footer-split">
              <button
                className={`btn-submit btn-complete-center ${!formData.completed && !isAllSubTasksCompleted() ? 'is-disabled' : ''}`}
                onClick={handleComplete}
                disabled={!formData.completed && !isAllSubTasksCompleted()}
              >
                {formData.completed ? '완료됨' : '업무 완료'}
              </button>
              <div className="btn-group-right">
                <button className="btn-cancel" onClick={handleDelete}>
                  삭제
                </button>
                <button
                  className="btn-submit"
                  onClick={handleSubmit}
                  disabled={formData.completed}
                >
                  수정
                </button>
              </div>
            </div>
          )}

          {/* 생성 모드: 취소/등록 버튼 */}
          {!isEditMode && (
            <div className="popup-footer">
              <button className="btn-cancel" onClick={onClose}>
                취소
              </button>
              <button
                className="btn-submit"
                disabled={!isFormValid()}
                onClick={handleSubmit}
              >
                등록
              </button>
            </div>
          )}
        </Spin>
      </AopsCommonModalLayout>
    </ConfigProvider>
  )
}

export default StandaloneScheduledTaskPopup
